import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useLiveUpdates } from '../../data/live/useLiveUpdates';
import { RouteTransitionProvider } from '../nav/RouteTransition';
import { rememberAppPath } from '../nav/useBack';
import { IS_REVIEW_BUILD } from '../reviewMode';
import { useApplyTheme, useSyncAppearance } from '../session';
import { CommandMenu } from './CommandMenu';
import { startInputModality } from './inputModality';
import { MobileNav } from './MobileNav';
import { Sidebar } from './Sidebar';
import { lastAppPathAtom } from './shellState';

/**
 * The application frame. Two genuinely different layouts, not one layout with
 * different padding:
 *  - phone: a large page title, full-width content, a fixed bottom nav, sheets;
 *  - desktop: a persistent sidebar and no bottom bar at all.
 *
 * Content is padded by the --gutter token, which itself widens at the desktop
 * breakpoint, so screens never hard-code page margins.
 */
export function AppShell() {
  useApplyTheme();
  /**
   * THE PER-PERSON APPEARANCE, ADOPTED HERE and nowhere else: inside the guard, so there
   * is a person to have a preference, and once per app. It runs AFTER `useApplyTheme`
   * mounts on purpose - storage has already painted the right theme, and this only
   * corrects it if the same person chose differently on another machine.
   */
  useSyncAppearance();
  /**
   * The live stream is opened HERE and nowhere else: inside the guard (so it always has a
   * session) and once per app (so a tab holds one connection, not one per screen).
   *
   * A review build has no server to stream from, and EventSource would spend the
   * session reconnecting to a refused port.
   */
  useLiveUpdates({ enabled: !IS_REVIEW_BUILD });
  const location = useLocation();
  const rememberLastAppPath = useSetAtom(lastAppPathAtom);
  const rememberedRef = useRef<string | null>(null);
  const pushedRecord = /^\/objects\/[^/]+\/records\/[^/]+$/.test(location.pathname);
  /**
   * THE LAST APP PATH IS REMEMBERED TWICE, and the second one is what survives a document
   * load (SH-A8). The atom is what the settings toolbar's "back to the app" chevron reads
   * inside one page life; `sessionStorage` is what a back control can still read after a
   * deep link has replaced the document, which is the case that sent the owner to People.
   * Same value, same moment, two lifetimes - and the tab is the right scope for both,
   * because "where was this person before" is a question about this window.
   */
  const appPath =
    location.pathname.startsWith('/settings') || location.pathname === '/more'
      ? null
      : location.pathname + location.search;
  /**
   * THE TAB'S MEMORY IS WRITTEN DURING THE RENDER, not in an effect, and the reason is a
   * measured race rather than a preference: a person can leave a screen before the browser
   * has painted it - Playwright's `goto` resolves on `load` and the review round's back
   * check navigated twice in a row - and an effect that has not run yet has remembered
   * nothing. `Dialog` captures the element that opened it during render for exactly the
   * same reason. The write is idempotent and guarded by the last value, so a re-render
   * costs nothing; the ATOM stays in the effect below, because a state update during
   * render is not.
   */
  if (appPath !== null && appPath !== rememberedRef.current) {
    rememberedRef.current = appPath;
    rememberAppPath(appPath);
  }
  useEffect(() => {
    if (appPath !== null) rememberLastAppPath(appPath);
  }, [appPath, rememberLastAppPath]);
  /**
   * SH-A3: THE FOCUS RING IS THE KEYBOARD'S. The document remembers which device the
   * person last used and `index.css` draws the ring only for the keyboard - the rule,
   * its Apple source and why the browser's own heuristic is not enough are in
   * `inputModality.ts`. One listener pair for the whole app, mounted where the theme is.
   */
  useEffect(() => startInputModality(), []);
  useEffect(() => {
    const setFocus = () => {
      document.documentElement.dataset['windowFocused'] = String(document.hasFocus());
    };
    setFocus();
    window.addEventListener('focus', setFocus);
    window.addEventListener('blur', setFocus);
    return () => {
      window.removeEventListener('focus', setFocus);
      window.removeEventListener('blur', setFocus);
    };
  }, []);

  return (
    /**
     * THE PROVIDER SITS INSIDE THE SHELL, not around the router, and that is what makes
     * the chrome hold still: only what it wraps can be animated, and what it wraps is the
     * route content. The sidebar, the phone header, the tab bar and the floating create
     * button are rendered outside `<main>` and therefore outside the transition group.
     */
    <RouteTransitionProvider>
      <div className="min-h-dvh bg-canvas text-ink md:flex">
        <Sidebar />

        <div className="flex min-w-0 flex-1 flex-col">
          <main
            // `route-view` is what a push or a pop moves (ui/motion.css). The class is
            // inert until a transition is running: the `view-transition-name` behind it
            // is applied under `html[data-nav-direction]`, which exists for those ~240ms
            // and at no other time. It CARRIES THE CANVAS (BR-PERF): a snapshot of a view
            // with no background is transparent, and the moving screen showed the other
            // one through it for the whole slide (4 Oct measurement, section 1a). At rest
            // it is the ground the frame around it already paints.
            className="route-view shell-main min-w-0 flex-1 bg-canvas page-gutter md:pb-(--page-pad-bottom)"
            // The bottom padding keeps the last row clear of the fixed nav bar.
          >
            <Outlet />
          </main>
        </div>

        {!pushedRecord && <MobileNav />}
        <CommandMenu />
      </div>
    </RouteTransitionProvider>
  );
}
